import React from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import type { LaunchSecureDto, PatinfoNewRequestDto, PatinfoProfileDto, PatinfoPutRequestDto, PatinfoSearchTermDto } from '../../../types/api/patinfo';
import type { AccountPlaDto, AccountSraDto, AdminHtmlAnswerDto, MVarDto } from '../../../types/api';
import type { ClientTouchpointSchedule } from '../../../services/clients/ClientFormBuilder';
import type { ClientEnableFlags } from './ClientEnableFlags';
import { EDITOR_COLORS, editorText } from '../../../styles/editorTheme';
import { ManageClientsResultsList } from './ManageClientsResultsList';
import type { ViewReportParams } from './ProfileRowEditor';
import { ManageClientsAddBar } from './ManageClientsAddBar';
import { ManageClientsSearchBar, type ManageClientsSearchValues } from './ManageClientsSearchBar';

interface ManageClientsViewProps {
  values: ManageClientsSearchValues;
  onValuesChange: (values: ManageClientsSearchValues) => void;
  onSearch: (terms: PatinfoSearchTermDto[], values: ManageClientsSearchValues) => void;
  profiles: PatinfoProfileDto[];
  isLoading: boolean;
  error: string | null;
  hasSearched: boolean;
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
  saveSyncError: string | null;
  enableFlags: ClientEnableFlags;
  onNextPage: () => void;
  onPrevPage: () => void;
  onSaveProfile: (payload: PatinfoPutRequestDto) => Promise<void>;
  onSendNow: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onCancelLaunch: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onViewReport?: (params: ViewReportParams) => void;
  isAddMode?: boolean;
  onAdd?: (payload: PatinfoNewRequestDto) => Promise<void>;
  onCancelAdd?: () => void;
  showDob?: boolean;
  expandedProfileId?: string | null;
  sendAssessmentOptions?: AdminHtmlAnswerDto[];
  officeOnlineSraMembers?: AccountSraDto[];
  assessmentOptions?: AccountPlaDto[];
  clientPhiFields?: MVarDto[];
  genderPhiOptions?: AdminHtmlAnswerDto[];
  touchpointSchedules?: ClientTouchpointSchedule[];
}

export const ManageClientsView: React.FC<ManageClientsViewProps> = ({
  values,
  onValuesChange,
  onSearch,
  profiles,
  isLoading,
  error,
  hasSearched,
  blocknum,
  blocksize,
  hasMore,
  saveSyncError,
  enableFlags,
  onNextPage,
  onPrevPage,
  onSaveProfile,
  onSendNow,
  onCancelLaunch,
  onViewReport,
  isAddMode = false,
  onAdd,
  onCancelAdd,
  showDob = true,
  expandedProfileId = null,
  sendAssessmentOptions = [],
  officeOnlineSraMembers = [],
  assessmentOptions = [],
  clientPhiFields = [],
  genderPhiOptions = [],
  touchpointSchedules = [],
}) => {
  const showPatId = enableFlags.enable_idmgmt;
  const content = (
    <>
      <View style={styles.searchBarWrap}>
        {isAddMode && onAdd && onCancelAdd ? (
          <ManageClientsAddBar onAdd={onAdd} onCancel={onCancelAdd} showPatId={showPatId} showDob={showDob} />
        ) : (
          <ManageClientsSearchBar
            values={values}
            onValuesChange={onValuesChange}
            onSearch={onSearch}
            isLoading={isLoading}
            showPatId={showPatId}
            showDob={showDob}
          />
        )}
      </View>

      {saveSyncError ? (
        <View style={styles.syncErrorBox}>
          <Text style={styles.syncErrorText}>{saveSyncError}</Text>
        </View>
      ) : null}

      {hasSearched ? (
        <ManageClientsResultsList
          profiles={profiles}
          isLoading={isLoading}
          error={error}
          blocknum={blocknum}
          blocksize={blocksize}
          hasMore={hasMore}
          enableFlags={enableFlags}
          onNextPage={onNextPage}
          onPrevPage={onPrevPage}
          onSaveProfile={onSaveProfile}
          onSendNow={onSendNow}
          onCancelLaunch={onCancelLaunch}
          onViewReport={onViewReport}
          expandedProfileId={expandedProfileId}
          sendAssessmentOptions={sendAssessmentOptions}
          officeOnlineSraMembers={officeOnlineSraMembers}
          assessmentOptions={assessmentOptions}
          clientPhiFields={clientPhiFields}
          genderPhiOptions={genderPhiOptions}
          touchpointSchedules={touchpointSchedules}
        />
      ) : (
        <View style={styles.promptState}>
          <Text style={styles.promptText}>
            Enter one or more search terms above and press Search to find clients.
          </Text>
        </View>
      )}
    </>
  );

  if (Platform.OS !== 'web') {
    return (
      <ScrollView
        contentContainerStyle={styles.panel}
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
      >
        {content}
      </ScrollView>
    );
  }

  return (
    <View style={styles.panel}>
      {content}
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    padding: 20,
    paddingBottom: 32,
  },
  searchBarWrap: {
    width: '100%',
    maxWidth: 980,
    alignSelf: 'center',
  },
  promptState: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  promptText: {
    ...editorText,
    color: EDITOR_COLORS.placeholder,
    textAlign: 'center',
    maxWidth: 400,
    lineHeight: 20,
  },
  syncErrorBox: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 10,
    padding: 12,
    marginBottom: 14,
  },
  syncErrorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 18,
  },
});
